import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Divider,
    Grid
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Security, QrCode, PhoneAndroid, ArrowBack } from "@mui/icons-material";

const TOTPAuthentication = ({ sessionToken, qrCodeImage, onCodeVerified, onError, onBack }) => {
    const [totpCode, setTotpCode] = useState("");
    const [loading, setLoading] = useState(false);

    const handleCodeSubmit = () => {
        if (totpCode.trim().length !== 6) {
            onError("Please enter a valid 6-digit code.");
            return;
        }

        setLoading(true);
        onCodeVerified(sessionToken, totpCode);

        // Reset loading state after a delay (will be handled by parent component)
        setTimeout(() => setLoading(false), 3000);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleCodeSubmit();
        }
    };

    return (
        <Box
            sx={{
                flex: 1,
                padding: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    padding: 4,
                    maxWidth: 900,
                    width: "100%",
                    textAlign: "center"
                }}
            >
                <Security
                    sx={{
                        fontSize: 60,
                        color: "#398DD2",
                        marginBottom: 2
                    }}
                />

                <Typography variant="h4" sx={{ mb: 2, color: "#398DD2" }}>
                    Two-Factor Authentication
                </Typography>

                <Typography variant="body1" sx={{ mb: 4, color: "#666" }}>
                    Complete your login using your mobile authenticator app
                </Typography>

                {/* Side by side layout */}
                <Grid container spacing={4} sx={{ mb: 4 }}>
                    {/* Manual Code Entry - Left Side */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                justifyContent: "center"
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <PhoneAndroid sx={{ mr: 1 }} />
                                Enter Authentication Code
                            </Typography>

                            <TextField
                                label="6-digit code"
                                variant="outlined"
                                fullWidth
                                value={totpCode}
                                onChange={(event) => setTotpCode(event.target.value)}
                                onKeyPress={handleKeyPress}
                                inputProps={{
                                    maxLength: 6,
                                    style: {
                                        textAlign: "center",
                                        fontSize: "24px",
                                        letterSpacing: "8px"
                                    }
                                }}
                                sx={{ mb: 3 }}
                                placeholder="000000"
                            />

                            <LoadingButton
                                onClick={handleCodeSubmit}
                                loading={loading}
                                variant="contained"
                                fullWidth
                                sx={{
                                    backgroundColor: "#398DD2",
                                    color: "#FFF",
                                    py: 2,
                                    fontSize: "16px"
                                }}
                            >
                                Verify Code
                            </LoadingButton>
                        </Box>
                    </Grid>

                    {/* Vertical Divider */}
                    <Grid item xs={12} md={6} sx={{ position: "relative" }}>
                        <Box
                            sx={{
                                position: "absolute",
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                height: "80%",
                                width: "1px",
                                backgroundColor: "#e0e0e0",
                                display: { xs: "none", md: "block" }
                            }}
                        />

                         OR text for mobile
                        <Box sx={{ display: { xs: "block", md: "none" }, textAlign: "center", my: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                OR
                            </Typography>
                        </Box>

                         QR Code Section - Right Side
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                                pl: { md: 3 }
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <QrCode sx={{ mr: 1 }} />
                                Scan with Mobile App
                            </Typography>

                            {qrCodeImage && (
                                <Box sx={{ mb: 3 }}>
                                    <img
                                        src={qrCodeImage.startsWith('data:') ? qrCodeImage : `data:image/png;base64,${qrCodeImage}`}
                                        alt="Login QR Code"
                                        style={{
                                            width: "220px",
                                            height: "220px",
                                            border: "2px solid #e0e0e0",
                                            borderRadius: "12px",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                                        }}
                                    />
                                </Box>
                            )}

                            <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", maxWidth: "280px" }}>
                                Open your mobile authenticator app and scan this QR code to automatically authenticate
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* OR text for desktop - positioned between columns */}
                <Box
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        padding: "8px 16px",
                        borderRadius: "20px",
                        border: "1px solid #e0e0e0",
                        display: { xs: "none", md: "block" }
                    }}
                >
                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: "bold" }}>
                        OR
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Button
                    variant="text"
                    onClick={onBack}
                    startIcon={<ArrowBack />}
                    sx={{ color: "#666" }}
                >
                    Back to Login
                </Button>
            </Paper>
        </Box>
    );
};

export default TOTPAuthentication;